// Aerodynamic Force Calculator tool: field metadata, vehicle/air-density presets, and
// client-side field validation, used for instant inline feedback as the user types. The
// actual math, the chart sweep, the reference tables and the authoritative (server-side)
// validation all live in the main backend — see src/lib/toolsApi.js
// (aerodynamicForceToolApi) and backend/tools/aerodynamic_force_tool.py.

export const DEFAULT_INPUTS = {
  vehicleSpeedKmph: 100,
  windSpeedKmph: 0,
  windAngleDeg: 0,
  dragCoefficient: 0.28,
  frontalAreaM2: 2.20,
  yawSensitivity: 2.5,
  drivetrainEfficiencyPct: 90,
  airDensityKgM3: 1.225,
  altitudeM: 0,
  ambientTempC: 15,
  relativeHumidityPct: 0,
};

// Mirrors FIELD_SPECS / DENSITY_VALUE_SPECS / ATMOSPHERE_SPECS in
// backend/core/lookup/aerodynamic_force_specs.py exactly.
export const FIELD_SPECS = [
  { key: 'vehicleSpeedKmph', label: 'Vehicle Speed', unit: 'km/h', min: 0, max: 350, strictlyPositive: false },
  { key: 'windSpeedKmph', label: 'Wind Speed', unit: 'km/h', min: 0, max: 150, strictlyPositive: false },
  { key: 'windAngleDeg', label: 'Wind Angle', unit: '°', min: 0, max: 180, strictlyPositive: false },
  { key: 'dragCoefficient', label: 'Drag Coefficient', unit: 'Cd', min: 0.15, max: 1.20, strictlyPositive: true },
  { key: 'frontalAreaM2', label: 'Frontal Area', unit: 'm²', min: 0.40, max: 12.0, strictlyPositive: true },
  { key: 'yawSensitivity', label: 'Yaw Sensitivity', unit: 'k', min: 0, max: 10, strictlyPositive: false },
  { key: 'drivetrainEfficiencyPct', label: 'Drivetrain Efficiency', unit: '%', min: 50, max: 100, strictlyPositive: true },
];

export const DENSITY_VALUE_SPECS = [
  { key: 'airDensityKgM3', label: 'Air Density', unit: 'kg/m³', min: 0.70, max: 1.55, strictlyPositive: true },
];

export const ATMOSPHERE_SPECS = [
  { key: 'altitudeM', label: 'Altitude', unit: 'm', min: -500, max: 5500, strictlyPositive: false },
  { key: 'ambientTempC', label: 'Ambient Temperature', unit: '°C', min: -40, max: 55, strictlyPositive: false },
  { key: 'relativeHumidityPct', label: 'Relative Humidity', unit: '%', min: 0, max: 100, strictlyPositive: false },
];

export const ALL_SPECS = [...FIELD_SPECS, ...DENSITY_VALUE_SPECS, ...ATMOSPHERE_SPECS];

// Quick-pick wind directions; any other angle shows as "Custom Angle".
export const WIND_DIRECTIONS = [
  { value: 'headwind', label: 'Headwind', angle: 0, note: 'Air meets the vehicle head-on (0°) — relative air speed and drag increase.' },
  { value: 'crosswind', label: 'Crosswind', angle: 90, note: 'Wind blows across the vehicle (90°) — the vehicle sees air at a yaw angle, which raises drag through both the higher relative speed and a higher yaw-dependent drag coefficient.' },
  { value: 'tailwind', label: 'Tailwind', angle: 180, note: 'Wind follows the vehicle (180°) — drag falls; if the wind is faster than the vehicle the force reverses and the wind assists motion.' },
  { value: 'custom', label: 'Custom Angle', angle: null, note: 'Wind angle measured from dead-ahead: 0° = headwind, 90° = crosswind, 180° = tailwind. 45° is a quartering headwind, 135° a quartering tailwind.' },
];

export const windDirectionForAngle = (angle) => {
  const match = WIND_DIRECTIONS.find((w) => w.angle !== null && Number(angle) === w.angle);
  return match ? match.value : 'custom';
};

// Typical Cd + frontal area + estimated yaw sensitivity by vehicle category.
// Representative figures, not a specific manufacturer/model measurement. Mirrors
// VEHICLE_PRESETS in backend/core/lookup/aerodynamic_force_specs.py exactly.
export const VEHICLE_PRESETS = [
  { key: 'motorcycle', label: 'Motorcycle (with rider)', dragCoefficient: 0.60, frontalAreaM2: 0.70, yawSensitivity: 1.5 },
  { key: 'hatchback', label: 'Hatchback', dragCoefficient: 0.32, frontalAreaM2: 2.05, yawSensitivity: 2.5 },
  { key: 'sedan', label: 'Passenger Sedan', dragCoefficient: 0.30, frontalAreaM2: 2.20, yawSensitivity: 2.5 },
  { key: 'ev', label: 'Electric Sedan', dragCoefficient: 0.23, frontalAreaM2: 2.30, yawSensitivity: 2.5 },
  { key: 'performance', label: 'Performance / Sports Car', dragCoefficient: 0.33, frontalAreaM2: 1.90, yawSensitivity: 2.5 },
  { key: 'suv', label: 'SUV', dragCoefficient: 0.35, frontalAreaM2: 2.80, yawSensitivity: 3.0 },
  { key: 'pickup', label: 'Pickup Truck', dragCoefficient: 0.45, frontalAreaM2: 3.00, yawSensitivity: 3.0 },
  { key: 'van', label: 'Van / LCV', dragCoefficient: 0.38, frontalAreaM2: 3.90, yawSensitivity: 3.5 },
  { key: 'bus', label: 'City Bus / Coach', dragCoefficient: 0.65, frontalAreaM2: 7.50, yawSensitivity: 5.0 },
  { key: 'truck', label: 'Heavy Truck (tractor-trailer)', dragCoefficient: 0.65, frontalAreaM2: 9.50, yawSensitivity: 5.0 },
  { key: 'custom', label: 'Custom', dragCoefficient: null, frontalAreaM2: null, yawSensitivity: null },
];

// Air density presets (ISA-derived, dry air). 'atmosphere' computes density server-side
// from altitude / temperature / humidity. Mirrors AIR_DENSITY_PRESETS in
// backend/core/lookup/aerodynamic_force_specs.py exactly.
export const AIR_DENSITY_PRESETS = [
  { key: 'sea_level', label: 'Sea Level, 15°C (Standard)', value: 1.225 },
  { key: 'hot_day', label: 'Hot Day, 35°C', value: 1.146 },
  { key: 'cold_day', label: 'Cold Day, -10°C', value: 1.341 },
  { key: 'high_altitude', label: 'High Altitude, ~2000m', value: 1.007 },
  { key: 'atmosphere', label: 'From Altitude & Temperature', value: null },
  { key: 'custom', label: 'Custom', value: null },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}
